import React, { ReactNode, useState } from "react";
import { Layout, Button, Drawer } from "antd";
import { IconMenu2 } from "@tabler/icons-react";
import { useAuth } from "../../contextProviders/AuthProvider";
import { useNavigate } from "react-router-dom";
import { theme } from "antd";
import { useTranslation } from "react-i18next";
import LanguageSwitcher from "../LanguageSwitcher";
import AppLogo from "../AppLogo";
import SiteFooter from "./SiteFooter";
import { CORAL } from "../../theme/creamyTokens";
import { TRANSLATIONS } from "../../constants/translations";
import { translate } from "../../helpers/translationHelper";
import { useAppBreakpoint } from "../../hooks/useAppBreakpoint";

const { Header, Content, Footer } = Layout;

interface LandingLayoutProps {
  children: ReactNode;
  // Mobile only, opt-in: skips this layout's own generic public header (and
  // the hamburger drawer it opens) for a screen that renders its own
  // dedicated fixed header instead -- see AppLayout's identical prop for the
  // signed-in equivalent.
  hideHeader?: boolean;
}

/**
 * Header+footer chrome for pages reachable while signed out (the homepage,
 * and any other public page's anonymous fallback -- see HeavyDutyMachineries/
 * HeavyDutyMachineryDetail). No longer redirects authenticated visitors away:
 * callers that also make sense for a signed-in user (Landing.tsx) render
 * through AppLayout instead once isAuthenticated, so this component is
 * simply never mounted for them.
 */
const LandingLayout: React.FC<LandingLayoutProps> = ({ children, hideHeader }) => {
  const { loading } = useAuth();
  const navigate = useNavigate();
  const { token } = theme.useToken();
  const { t } = useTranslation();
  // Same isMobile-collapse pattern as AppHeader (the signed-in header) --
  // this was the last header in the app still missing it: Spanish "Iniciar
  // sesión"/"Registrarse" are wide enough that the uncollapsed row could
  // clip against the language switcher on a 375px screen.
  const { isMobile } = useAppBreakpoint();
  const [drawerOpen, setDrawerOpen] = useState(false);

  if (loading) return <div>{translate(t, TRANSLATIONS.LAYOUT_LOADING)}</div>;

  return (
    <Layout style={{ minHeight: "100vh" }}>
      {/* Full-width Fixed Header */}
      {!hideHeader && (
      <Header
        style={{
          position: "fixed",
          width: "100%",
          zIndex: 1000,
          background: token.colorBgContainer,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          padding: "0 16px",
        }}
      >
        {/* Left: logo -- public pages like /heavy-duty-machinery render through this
            layout too (not just the homepage), so without it a signed-out
            visitor on a deep link has no way back to the site's entry point.
            AppLogo ya elige isotipo o bloque completo segun el ancho, asi que
            aqui no hay nada que decidir. */}
        <AppLogo />

        {isMobile ? (
          <Button
            type="text"
            // Same tabler outline-icon weight as the rest of the redesigned
            // mobile chrome (AppHeader, NotificationBell, etc.) instead of
            // antd's own bolder MenuOutlined glyph.
            icon={<IconMenu2 size={22} />}
            aria-label={translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_NAV_MENU_ARIA_LABEL)}
            onClick={() => setDrawerOpen(true)}
          />
        ) : (
          // Right: Language Switcher, Login & Sign up -- Sign up is the
          // filled/primary pill since creating an account is the action this
          // header wants to drive a signed-out visitor toward; Log In stays
          // an outlined pill for the (smaller) audience who already has one.
          <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
            <LanguageSwitcher />
            <Button style={{ borderRadius: 999 }} onClick={() => navigate("/login")}>
              {translate(t, TRANSLATIONS.LOG_IN)}
            </Button>
            <Button type="primary" style={{ borderRadius: 999 }} onClick={() => navigate("/register")}>
              {translate(t, TRANSLATIONS.LAYOUT_HEADER_SIGN_UP)}
            </Button>
          </div>
        )}
      </Header>
      )}

      {!hideHeader && (
      <Drawer
        // No logo mark in the title -- it linked to /dashboard, which makes
        // no sense from a drawer opened on the page it already points back
        // to (and /dashboard requires auth anyway for a signed-out visitor).
        // Bottom sheet instead of a side panel sliding in from the left --
        // rises from the bottom edge, not a full-height screen, just tall
        // enough to clear the language selector and the two auth buttons
        // (see height below).
        placement="bottom"
        onClose={() => setDrawerOpen(false)}
        open={drawerOpen}
        height={260}
        // Square corners (not rounded) and an upward-cast shadow on top of
        // antd's own mask -- the same "content sliding in underneath a
        // floating layer" depth cue as the header's own scroll shadow (see
        // AppHeader/useScrollChrome), just facing the opposite direction
        // since this sheet rises from the bottom instead of sitting fixed
        // at the top. Plain white background (SURFACE_WARM's warm tan was
        // tried in an earlier pass and read as too much color for this
        // panel -- reverted to the app's default surface).
        styles={{
          content: {
            borderRadius: 0,
            boxShadow: "0 -8px 24px rgba(0,0,0,0.12)",
          },
        }}
      >
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          <LanguageSwitcher />
          <Button
            block
            style={{ borderRadius: 999, borderColor: CORAL, color: CORAL, background: "transparent" }}
            onClick={() => {
              navigate("/login");
              setDrawerOpen(false);
            }}
          >
            {translate(t, TRANSLATIONS.LOG_IN)}
          </Button>
          <Button
            block
            type="primary"
            style={{ borderRadius: 999 }}
            onClick={() => {
              navigate("/register");
              setDrawerOpen(false);
            }}
          >
            {translate(t, TRANSLATIONS.LAYOUT_HEADER_SIGN_UP)}
          </Button>
        </div>
      </Drawer>
      )}

      {/* Main Content */}
      <Layout style={{ marginTop: hideHeader ? 0 : 64 }}>
        {/* Same flex centering as AppLayout's Content -- textAlign alone
            doesn't centre a block child, so pages with a maxWidth wrapper
            (e.g. /heavy-duty-machinery's anonymous fallback) used to sit hard against
            the left edge here while looking centred when signed in. */}
        <Content
          style={{
            padding: "24px",
            textAlign: "center",
            display: "flex",
            justifyContent: "center",
          }}
        >
          {children}
        </Content>
        <Footer>
          <SiteFooter />
        </Footer>
      </Layout>
    </Layout>
  );
};

export default LandingLayout;
